import { Box, Button, Flex, FormLabel, Image, Text, Tooltip } from "@chakra-ui/react";
import AlertTriangleIcon from "assets/img/profile/alert-triangle.svg";
import IconBox from "components/icons/IconBox";
import {
  IconPlus,
  IconCardDiscover,
  IconCardExpress,
  IconCardMaster,
  IconCardVisa,
} from "components/icons/Icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  // delBankRequest,
  getCardsRequest,
  removeCardInfoRequest,
} from "store/user/reducer";
import {
  cardsSelector,
  userSelector,
} from "store/user/selector";
// import BankInputForm from "views/customer/order/components/payment/BankInputForm";
import { loadStripe, Stripe, StripeCardNumberElement } from "@stripe/stripe-js";
import { axiosService } from "network/axios";
import { AuthInput } from "components/auth/AuthInput";
import { AddIcon } from "@chakra-ui/icons";

const REACT_APP_STRIPE_PUBLISHABLE = process.env.REACT_APP_STRIPE_PUBLISHABLE;

export default function CreditCards() {
  const dispatch = useDispatch();
  const [openCardInput, setOpenCardInput] = useState(false);
  // const [openAddressForm, setOepnAddressForm] = useState(false);
  const cards = useSelector(cardsSelector);
  const user = useSelector(userSelector);
  // const banks = useSelector(bankAccountsSelector);
  const [stripe, setStripe] = useState<Stripe>(null);
  const [cardNumberElement, setCardNumberElement] = useState<StripeCardNumberElement>(null);
  const [cardBrand, setCardBrand] = useState<string>("");
  const [cardHolderName, setCardHolderName] = useState<string>("");
  const [isSavingCardInfo, setIsSavingCardInfo] = useState<boolean>(false);
  const [cardUpdateStatus, setCardUpdateStatus] = useState<"success" | "error" | null>(null);
  const [cardUpdateMessage, setCardUpdateMessage] = useState<string>("");

  const [isCardNumberFocused, setIsCardNumberFocused] = useState(false);
  const [isCardExpiryFocused, setIsCardExpiryFocused] = useState(false);
  const [isCardCvcFocused, setIsCardCvcFocused] = useState(false);



  
  const individualCards = useMemo(() => {
    if (user && cards?.length > 0) {
      return cards?.filter(
        (it) => Number(it?.user_id) === Number(user.user_id)
      );
    } else {
      return [];
    }
  }, [cards, user]);

  const onRemoveCard = (cardId: number) => {
    dispatch(
      removeCardInfoRequest({
        user_id: user.user_id,
        card_id: cardId,
      })
    );
  };

  const getCardBrandLabel = (brand?: string) => {
    const b = String(brand || "").toLowerCase();
    if (b === "visa") return "Visa";
    if (b === "mastercard") return "Mastercard";
    if (b === "americanexpress") return "American Express";
    if (b === "discover") return "Discover";
    return "Card";
  };

  // const onRemoveBank = (bank: any) => {
  //   dispatch(
  //     delBankRequest({
  //       user_id: user.user_id,
  //       bank_id: bank.id,
  //     })
  //   );
  // };

  useEffect(() => {
    setOpenCardInput(false);
  }, [cards]);

  useEffect(() => {
    if (openCardInput) {
      initStripe();
    } else {
      setStripe(null);
      setCardNumberElement(null);
      setCardBrand("");
    }
  }, [openCardInput]);

  const initStripe = async () => {
    let stripe = await loadStripe(REACT_APP_STRIPE_PUBLISHABLE);
    setStripe(stripe);
    var elements = stripe.elements();
    
    const elementStyle = {
      base: {
        iconColor: "#0B0E10",
        color: "#0B0E10",
        fontWeight: 400,
        fontFamily: "General Sans, Inter, sans-serif",
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#9CA3AF",
        },
        ":-webkit-autofill": {
          color: "#777",
        },
      },
      invalid: {
        iconColor: "#F00",
        color: "#F00",
      },
    };

    // Card Number Element
    let cardNumberEl = elements.create("cardNumber", {
      style: elementStyle,
      placeholder: "1234 1234 1234 1234",
    });
    cardNumberEl.mount("#card-number-element");
    
    // Card Expiry Element
    let cardExpiryEl = elements.create("cardExpiry", {
      style: elementStyle,
      placeholder: "06/28",
    });
    cardExpiryEl.mount("#card-expiry-element");
    
    // Card CVC Element
    let cardCvcEl = elements.create("cardCvc", {
      style: elementStyle,
      placeholder: "369",
    });
    cardCvcEl.mount("#card-cvc-element");

    cardNumberEl.on("focus", () => setIsCardNumberFocused(true));
    cardNumberEl.on("blur", () => setIsCardNumberFocused(false));
    cardExpiryEl.on("focus", () => setIsCardExpiryFocused(true));
    cardExpiryEl.on("blur", () => setIsCardExpiryFocused(false));
    cardCvcEl.on("focus", () => setIsCardCvcFocused(true));
    cardCvcEl.on("blur", () => setIsCardCvcFocused(false));

    let isCardNumberComplete = false;
    let isCardExpiryComplete = false;
    let isCardCvcComplete = false;

    cardNumberEl.on("change", function (event) {
      isCardNumberComplete = event.complete;
      if (event.brand) {
        setCardBrand(event.brand);
      }
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });

    cardExpiryEl.on("change", function (event) {
      isCardExpiryComplete = event.complete;
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });

    cardCvcEl.on("change", function (event) {
      isCardCvcComplete = event.complete;
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });
  };

  const saveCardInformation = () => {
    setIsSavingCardInfo(true);
    setCardUpdateStatus(null);
    setCardUpdateMessage("");
    
    stripe
      .createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: cardHolderName,
        },
      })
      .then(async function (result) {
        if (result.error) {
          setCardUpdateStatus("error");
          setCardUpdateMessage(result.error.message || "Unable to save card details. Please review and try again.");
          setIsSavingCardInfo(false);
        } else {
          const paymentMethodId = result?.paymentMethod?.id;
          try {
            await axiosService.post(
              `/shipping/put/payment-method/${user.user_id}`,
              {
                payment_method_id: paymentMethodId,
                is_default: 1,
              }
            );
            setCardUpdateStatus("success");
            setCardUpdateMessage("Your card has been updated successfully.");
            dispatch(getCardsRequest({ user_id: user.user_id }));
            setTimeout(() => {
              setOpenCardInput(false);
              setCardHolderName("");
              setCardUpdateStatus(null);
            }, 2000);
          } catch (error: any) {
            setCardUpdateStatus("error");
            const errorMessage = error?.data?.messages?.error || error?.response?.data?.messages?.error || "Unable to save card details. Please review and try again.";
            setCardUpdateMessage(errorMessage);
          }
          setIsSavingCardInfo(false);
        }
      })
      .catch((error: any) => {
        setCardUpdateStatus("error");
        const errorMessage = error?.data?.messages?.error || error?.response?.data?.messages?.error || "Unable to save card details. Please review and try again.";
        setCardUpdateMessage(errorMessage);
        setIsSavingCardInfo(false);
      });
  };

  return (
    <Box>
      <Box
        p={{ md: "24px", base: "20px" }}
        border="1px solid #E6ECF3"
        borderRadius="12px"
        bg="white"
        w="full"
        maxW={{ base: "full", md: "690px" }}
        mb="24px"
      >
        <Text fontSize="16px" fontWeight="600" color="#0B0E10">
          Saved credit cards
        </Text>
        <Text fontSize="14px" color="#0B0E10" mb="16px">
          Manage the cards you use for payments
        </Text>

        {individualCards?.length > 0 ? (
          <>
            <Box p={{ md: "16px", base: "12px" }} border="1px solid #D2DAE5" borderRadius="12px">
              {/* Saved Credit Cards List */}
              <Box>
                {individualCards.map((card: any, index: number) => (
                  <Flex
                      padding="10px 14px"
                      alignItems="center"
                      border="1px solid #7C8FA3"
                      borderRadius="12px"
                      mb={index === individualCards.length - 1 ? "0px" : "12px"}
                      bg="white"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ bg: "#F8FAFC", borderColor: "#3A7BFF" }}
                    >
                      <Tooltip
                        key={index}
                        label={getCardBrandLabel(card?.card_brand)}
                        placement="top"
                        hasArrow
                        shouldWrapChildren
                      >
                        <IconBox
                          mr="16px"
                          w="40px"
                          h="25px"
                          icon={
                            card?.card_brand === "visa" ? (
                              <IconCardVisa w="40px" h="25px" />
                            ) : card?.card_brand === "mastercard" ? (
                              <IconCardMaster w="40px" h="25px" />
                            ) : card?.card_brand === "americanexpress" ? (
                              <IconCardExpress w="40px" h="25px" />
                            ) : card?.card_brand === "discover" ? (
                              <IconCardDiscover w="40px" h="25px" />
                            ) : (
                              ""
                            )
                          }
                        />
                        </Tooltip>
                        <Box flex="1">
                        <Text
                          fontSize="16px"
                          lineHeight="22px"
                          fontWeight="400"
                          color="#0B0E10"
                        >
                          ending in {card.card_number.slice(-4)}
                        </Text>
                      </Box>
                      <Flex alignItems="center" gap="12px">
                        <Button
                          variant="link"
                          fontSize="14px"
                          fontWeight="600"
                          color="#3A7BFF"
                          _hover={{ textDecoration: "underline" }}
                          onClick={() => onRemoveCard(card.id)}
                        >
                          Delete
                        </Button>
                      </Flex>
                    </Flex>
                ))}
              </Box>
            </Box>
            {/* Add Credit Card Button */}
            <Button
              leftIcon={<AddIcon w="14px" h="14px" boxSize="14px" />}
              bgColor="#3A7BFF"
              w={{ base: "full", md: "211px" }}
              h="40px"
              mt="16px"
              color="#FFF"
              fontSize="14px"
              fontWeight="600"
              borderRadius="100px"
              padding="12px 20px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              onClick={() => setOpenCardInput(true)}
            >
              Add a credit card
            </Button>
          </>
        ) : (
          <Button
            leftIcon={<IconPlus w="20px" h="20px" />}
            bgColor="#3A7BFF"
            w={{ base: "full", md: "211px" }}
            h="48px"
            color="#FFF"
            fontSize="14px"
            fontWeight="700"
            borderRadius="100px"
            padding="12px 20px"
            border="1px solid #2C63E5"
            _hover={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
            _active={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
            onClick={() => setOpenCardInput(true)}
          >
            Add a credit card
          </Button>
        )}
      </Box>
      



      {openCardInput && (
        <Box
          mt="24px"
          p="24px"
          border="1px solid #E6ECF3"
          borderRadius="12px"
          bg="white"
          w="full"
          maxW={{ base: "full", md: "690px" }}
        >
          <Text
            fontSize="16px"
            fontWeight="600"
            color="#0B0E10"
          >
            Add a credit card
          </Text>
          <Text
            fontSize="14px"
            color="#0B0E10"
            mb="16px"
          >
            Add a new payment method to your account.
          </Text>

          {/* Success Message */}
          {cardUpdateStatus === "success" && (
            <Box
              p="12px 16px"
              bg="#F0FDF4"
              border="1px solid #BBF7D0"
              borderRadius="8px"
              mb="20px"
            >
              <Flex align="center" gap="8px">
                <Box color="#16A34B" fontSize="16px">✓</Box>
                <Text fontSize="14px" color="#166534">
                  {cardUpdateMessage}
                </Text>
              </Flex>
            </Box>
          )}

          {/* Error Message */}
          {cardUpdateStatus === "error" && (
            <Box
              bg="#FEF2F2"
              borderRadius="8px"
              p="16px"
              mb="20px"
            >
              <Flex alignItems="center" columnGap="8px">
                <Image src={AlertTriangleIcon} alt="" w="18px" h="18px" />
                <Text fontSize="16px" color="#000000">
                  {cardUpdateMessage}
                </Text>
              </Flex>
            </Box>
          )}

          {/* Card Number Field */}
          <Box border="1px solid #D2DAE5" p="16px" borderRadius="12px">
          <Box mb="20px" >
            <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="16px">
              Card number
            </FormLabel>
            <Box position="relative">
              <Box
                padding="12px 16px"
                paddingRight="140px"
                borderRadius="12px"
                border={
                  isCardNumberFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                position="relative"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="card-number-element" w="100%"></Box>
              </Box>
              {/* Card Brand Icons */}
              <Flex
                position="absolute"
                right="16px"
                top="50%"
                transform="translateY(-50%)"
                gap="6px"
                alignItems="center"
              >
                <Box opacity={cardBrand === "visa" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardVisa w="32px" h="20px" />
                </Box>
                <Box opacity={cardBrand === "amex" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardExpress w="32px" h="20px" />
                </Box>
                <Box opacity={cardBrand === "discover" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardDiscover w="32px" h="20px" />
                </Box>
                <Box opacity={cardBrand === "mastercard" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardMaster w="32px" h="20px" />
                </Box>
              </Flex>
            </Box>
          </Box>

          {/* Name on Card Field */}
          <Box mb="20px">
            <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
              Name on card
            </FormLabel>
            <AuthInput
              name="name-on-card"
              role="customer"
              chakraVariant="authCustomerNew"
              placeholder="John Doe"
              value={cardHolderName}
              onChange={(e) => {
                setCardHolderName(e.target.value);
              }}
            />
          </Box>

          {/* Expiration Date and CVV */}
          <Flex gap="16px" mb="24px" direction={{ base: "column", md: "row" }}>
            <Box flex="1">
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                Expiration date
              </FormLabel>
              <Box
                padding="12px 16px"
                borderRadius="12px"
                border={
                  isCardExpiryFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="card-expiry-element" w="100%"></Box>
              </Box>
            </Box>
            <Box flex="1">
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                CVV
              </FormLabel>
              <Box
                padding="12px 16px"
                borderRadius="12px"
                border={
                  isCardCvcFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="card-cvc-element" w="100%"></Box>
              </Box>
            </Box>
          </Flex>

          {/* Action Buttons */}
          <Flex gap="12px" justifyContent="flex-start" direction={{ base: "column", md: "row" }}>
            <Button
            w={{ base: "full", md: "192px" }}
            h="40px"
              bg="#3A7BFF"
              color="white"
              fontSize="14px"
              fontWeight="500"
              borderRadius="24px"
              padding="12px 24px"
              height="40px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              // disabled={
              //   !stripe || !cardElement || !cardHolderName || !cardZipCode
              // }
              _disabled={{
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              }}
              onClick={() => {
                saveCardInformation();
              }}
              isLoading={isSavingCardInfo}
            >
              Save changes
            </Button>
            <Button
              variant="ghost"
              fontSize="14px"
              fontWeight="500"
              color="#3A7BFF"
              borderRadius="24px"
              border="1px solid #3A7BFF"
              w={{ base: "full", md: "auto" }}
              _hover={{
                color: "#fff",
                borderColor: "#3A7BFF",
                bgColor: "#3A7BFF",
              }}
              _active={{
                color: "#fff",
                borderColor: "#3A7BFF",
                bgColor: "#3A7BFF",
              }}
              onClick={() => {
                setOpenCardInput(false);
                setCardHolderName("");
                setCardUpdateStatus(null);
                setCardUpdateMessage("");
              }}
            >
              Cancel
            </Button>
          </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
}
